import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../contractorApi';

const NEXT_ACTION = {
    chatting:    { label: '見積書を提出する',   path: (id) => `/contractor/estimate/create/${id}` },
    decided:     { label: '請求書を提出する',   path: (id) => `/contractor/invoice/create/${id}` },
    paid:        { label: '工事日程を登録する', path: (id) => `/contractor/schedule/${id}` },
    scheduled:   { label: '工事開始を記録する', path: (id) => `/contractor/project/${id}` },
    in_progress: { label: '完了通知を送る',     path: (id) => `/contractor/project/${id}` },
};

const WAITING_LABEL = {
    estimated:           '顧客が業者を選定中です',
    invoiced:            '顧客の支払いを待っています',
    completion_notified: '入金処理を待っています',
};

const ArrowIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);

const EmptyIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" ry="1" />
        <line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" />
    </svg>
);

const ContractorProgressPage = () => {
    const contractorId = localStorage.getItem('contractor_id');
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`projects/by_contractor/${contractorId}`)
            .then(r => setProjects(r.data.filter(p => !['lost', 'completed'].includes(p.status))))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [contractorId]);

    const actionNeeded = projects.filter(p => NEXT_ACTION[p.status]);
    const waiting      = projects.filter(p => !NEXT_ACTION[p.status]);

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
    );

    return (
        <div style={{ minHeight: '80vh', background: '#f5f6f8' }}>

            {/* ページヘッダー */}
            <div style={{ background: '#141c3d', padding: '36px 0' }}>
                <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 16px' }}>
                    <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                        やることリスト
                    </h1>
                </div>
            </div>

            <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 16px' }}>

                {/* 空の状態 */}
                {projects.length === 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0' }}>
                        <div style={{
                            width: 96, height: 96, background: 'rgba(0,92,83,0.08)',
                            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            marginBottom: 24,
                        }}>
                            <EmptyIcon />
                        </div>
                        <p style={{ fontSize: '1rem', color: '#6b7280', margin: 0 }}>現在対応中の案件はありません</p>
                    </div>
                )}

                {/* 要対応 */}
                {actionNeeded.length > 0 && (
                    <div style={{ marginBottom: 36 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>要対応</h2>
                            <span style={{
                                minWidth: 28, height: 28, padding: '0 10px',
                                background: '#005c53', color: '#fff',
                                borderRadius: 14, fontSize: '0.82rem', fontWeight: 600,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {actionNeeded.length}
                            </span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {actionNeeded.map(project => {
                                const action = NEXT_ACTION[project.status];
                                const isDecided = project.status === 'decided';
                                const customerName = project.customer
                                    ? `${project.customer.last_name} ${project.customer.first_name} 様`
                                    : `顧客 #${project.customer_id}`;
                                const workType = project.condition?.work_types?.join('・') || '';

                                return (
                                    <div key={project.id} style={{
                                        background: isDecided ? '#fffbeb' : '#fff',
                                        borderRadius: 8,
                                        borderLeft: `3px solid ${isDecided ? '#f59e0b' : '#005c53'}`,
                                        boxShadow: isDecided ? '0 2px 8px rgba(245,158,11,0.15)' : '0 1px 4px rgba(0,0,0,0.08)',
                                        padding: '20px 24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 16,
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                                                <span style={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e' }}>
                                                    {customerName}
                                                </span>
                                                {isDecided && (
                                                    <span style={{ padding: '2px 10px', background: '#f59e0b', color: '#fff', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700 }}>
                                                        受注確定
                                                    </span>
                                                )}
                                                <span style={{ fontSize: '0.82rem', color: isDecided ? '#92400e' : '#005c53', fontWeight: 500 }}>
                                                    {action.label}
                                                </span>
                                            </div>
                                            {isDecided && (
                                                <p style={{ fontSize: '0.83rem', color: '#92400e', margin: '4px 0 0' }}>
                                                    受注が確定しました。請求書を送りましょう。
                                                </p>
                                            )}
                                            {workType && (
                                                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: isDecided ? '2px 0 0' : 0 }}>{workType}</p>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                            <button
                                                onClick={() => navigate(`/contractor/project/${project.id}`)}
                                                style={{
                                                    padding: '8px 14px', background: '#fff', color: '#6b7280',
                                                    border: '1px solid #e0e4ea', borderRadius: 4,
                                                    fontSize: '0.82rem', cursor: 'pointer',
                                                }}>
                                                詳細
                                            </button>
                                            <button
                                                onClick={() => navigate(action.path(project.id))}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                    padding: '8px 18px',
                                                    background: isDecided ? '#f59e0b' : '#005c53',
                                                    color: '#fff',
                                                    border: 'none', borderRadius: 4,
                                                    fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                対応する <ArrowIcon />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* 待機中 */}
                {waiting.length > 0 && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>待機中</h2>
                            <span style={{
                                minWidth: 28, height: 28, padding: '0 10px',
                                background: '#6b7280', color: '#fff',
                                borderRadius: 14, fontSize: '0.82rem', fontWeight: 600,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {waiting.length}
                            </span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {waiting.map(project => {
                                const waitingMsg = WAITING_LABEL[project.status];
                                const customerName = project.customer
                                    ? `${project.customer.last_name} ${project.customer.first_name} 様`
                                    : `顧客 #${project.customer_id}`;
                                const workType = project.condition?.work_types?.join('・') || '';

                                return (
                                    <div key={project.id} style={{
                                        background: '#fff',
                                        borderRadius: 8,
                                        borderLeft: '3px solid #e0e4ea',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        padding: '20px 24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 16,
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                                                <span style={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e' }}>
                                                    {customerName}
                                                </span>
                                                {waitingMsg && (
                                                    <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                                                        {waitingMsg}
                                                    </span>
                                                )}
                                            </div>
                                            {workType && (
                                                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>{workType}</p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => navigate(`/contractor/chat/${project.id}`)}
                                            style={{
                                                padding: '8px 14px', background: '#fff', color: '#6b7280',
                                                border: '1px solid #e0e4ea', borderRadius: 4,
                                                fontSize: '0.82rem', cursor: 'pointer', flexShrink: 0,
                                            }}>
                                            チャット
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default ContractorProgressPage;
